import type { SimEvent } from '../simulation/types';

type AircraftAudioProfile = {
  baseHz: number;
  harmonicHz: number;
  waveform: OscillatorType;
  harmonicWaveform: OscillatorType;
  filterHz: number;
  drive: number;
  afterburnerBoost: number;
};

type AudioUpdate = {
  screen: 'home' | 'hangar' | 'battle';
  paused: boolean;
  throttle: number;
  afterburner: number;
  speed: number;
  lockWarningActive: boolean;
};

export class AudioSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineBus: GainNode | null = null;
  private fxBus: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engineCore: OscillatorNode | null = null;
  private engineHarmonic: OscillatorNode | null = null;
  private engineCoreGain: GainNode | null = null;
  private engineHarmonicGain: GainNode | null = null;
  private afterburnerGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private unlocked = false;
  private currentAircraftId = 'j15';
  private lockWarningLatched = false;

  unlock(): void {
    if (!this.context) this.createContext();
    if (!this.context || !this.master) return;
    if (this.context.state === 'suspended') void this.context.resume();
    this.unlocked = true;
  }

  setAircraft(aircraftId: string): void {
    this.currentAircraftId = aircraftId;
    if (!this.context) return;
    const profile = this.getProfile();
    if (this.engineCore) this.engineCore.type = profile.waveform;
    if (this.engineHarmonic) this.engineHarmonic.type = profile.harmonicWaveform;
    if (this.engineFilter) this.engineFilter.frequency.setTargetAtTime(profile.filterHz, this.context.currentTime, 0.25);
    if (this.engineCoreGain) this.engineCoreGain.gain.setTargetAtTime(profile.drive, this.context.currentTime, 0.25);
    if (this.engineHarmonicGain) this.engineHarmonicGain.gain.setTargetAtTime(profile.drive * 0.42, this.context.currentTime, 0.25);
  }

  update(state: AudioUpdate): void {
    if (!this.unlocked) return;
    if (!this.context || !this.master || !this.engineBus || !this.engineFilter || !this.engineCore || !this.engineHarmonic || !this.afterburnerGain) return;
    const now = this.context.currentTime;
    const profile = this.getProfile();
    const battleMix = state.screen === 'battle' ? 1 : state.screen === 'hangar' ? 0.18 : 0.06;
    const pauseMix = state.paused ? 0.32 : 1;
    const throttle = Math.max(0, Math.min(1, state.throttle));
    const speedBlend = Math.max(0, Math.min(1, state.speed / 220));
    const rpm = throttle * 0.72 + speedBlend * 0.28;
    const coreHz = profile.baseHz + rpm * 58;
    const harmonicHz = profile.harmonicHz + rpm * 92;
    const filterHz = profile.filterHz + rpm * 560 + state.afterburner * 420;
    const engineLevel = battleMix * pauseMix * (0.18 + throttle * 0.16);
    const burnerLevel = battleMix * pauseMix * state.afterburner * profile.afterburnerBoost;

    this.master.gain.setTargetAtTime(0.92, now, 0.18);
    this.engineBus.gain.setTargetAtTime(engineLevel, now, 0.12);
    this.engineFilter.frequency.setTargetAtTime(filterHz, now, 0.18);
    this.engineCore.frequency.setTargetAtTime(coreHz, now, 0.08);
    this.engineHarmonic.frequency.setTargetAtTime(harmonicHz, now, 0.08);
    this.afterburnerGain.gain.setTargetAtTime(burnerLevel, now, 0.1);

    if (state.lockWarningActive && !this.lockWarningLatched) {
      this.lockWarningLatched = true;
      this.playLockAlert();
    } else if (!state.lockWarningActive) {
      this.lockWarningLatched = false;
    }
  }

  handleEvents(events: SimEvent[]): void {
    if (!this.unlocked || !this.context) return;
    for (const event of events) {
      if (event.type === 'missile-fired' && event.faction === 'player') this.playMissileLaunch();
      if (event.type === 'gun-tracer' && event.faction === 'player') this.playCannonBurst();
      if (event.type === 'explosion') this.playExplosion(event.tone === 'large' ? 1 : 0.55);
      if (event.type === 'damage' && event.targetId === 'player') this.playImpact();
      if (event.type === 'message' && (event.text.includes('热焰弹') || event.text.includes('Flare'))) this.playFlareBurst();
      if (event.type === 'message' && (event.text.includes('导弹告警') || event.text.includes('雷达锁定'))) this.playLockAlert();
    }
  }

  playUiConfirm(): void {
    if (!this.unlocked) return;
    this.playToneSweep(660, 980, 0.05, 'triangle', 0.045);
  }

  private createContext(): void {
    this.context = new AudioContext();
    this.master = this.context.createGain();
    this.master.gain.value = 0.86;
    this.engineBus = this.context.createGain();
    this.engineBus.gain.value = 0.01;
    this.fxBus = this.context.createGain();
    this.fxBus.gain.value = 0.92;
    this.engineFilter = this.context.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 720;
    this.afterburnerGain = this.context.createGain();
    this.afterburnerGain.gain.value = 0;
    this.engineCoreGain = this.context.createGain();
    this.engineCoreGain.gain.value = 0.16;
    this.engineHarmonicGain = this.context.createGain();
    this.engineHarmonicGain.gain.value = 0.08;
    this.compressor = this.context.createDynamicsCompressor();
    this.compressor.threshold.value = -14;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 3;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.22;

    this.engineCore = this.context.createOscillator();
    this.engineHarmonic = this.context.createOscillator();
    this.engineCore.type = 'sawtooth';
    this.engineHarmonic.type = 'triangle';
    this.engineCore.frequency.value = 78;
    this.engineHarmonic.frequency.value = 132;

    const burnerNoise = this.createNoiseSource();
    const burnerFilter = this.context.createBiquadFilter();
    burnerFilter.type = 'bandpass';
    burnerFilter.frequency.value = 980;
    burnerFilter.Q.value = 0.9;

    this.engineCore.connect(this.engineCoreGain);
    this.engineHarmonic.connect(this.engineHarmonicGain);
    this.engineCoreGain.connect(this.engineFilter);
    this.engineHarmonicGain.connect(this.engineFilter);
    this.engineFilter.connect(this.engineBus);
    burnerNoise.connect(burnerFilter);
    burnerFilter.connect(this.afterburnerGain);
    this.afterburnerGain.connect(this.engineBus);
    this.engineBus.connect(this.master);
    this.fxBus.connect(this.master);
    this.master.connect(this.compressor);
    this.compressor.connect(this.context.destination);

    this.engineCore.start();
    this.engineHarmonic.start();
    burnerNoise.start();
    this.setAircraft(this.currentAircraftId);
  }

  private getProfile(): AircraftAudioProfile {
    if (this.currentAircraftId === 'fa18f') {
      return {
        baseHz: 92,
        harmonicHz: 164,
        waveform: 'triangle',
        harmonicWaveform: 'sawtooth',
        filterHz: 1180,
        drive: 0.13,
        afterburnerBoost: 0.12,
      };
    }
    return {
      baseHz: 74,
      harmonicHz: 128,
      waveform: 'sawtooth',
      harmonicWaveform: 'square',
      filterHz: 860,
      drive: 0.17,
      afterburnerBoost: 0.16,
    };
  }

  private createNoiseSource(): AudioBufferSourceNode {
    if (!this.context) throw new Error('AudioContext not initialized');
    const buffer = this.context.createBuffer(1, this.context.sampleRate * 2, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    return source;
  }

  private playMissileLaunch(): void {
    this.playToneSweep(180, 76, 0.32, 'sawtooth', 0.12);
    this.playNoiseBurst(0.12, 1400, 0.08, 0.16);
  }

  private playCannonBurst(): void {
    this.playNoiseBurst(0.035, 2100, 0.02, 0.08);
    this.playToneSweep(420, 260, 0.04, 'square', 0.03);
  }

  private playFlareBurst(): void {
    this.playNoiseBurst(0.14, 2600, 0.04, 0.1);
    this.playToneSweep(1120, 720, 0.18, 'triangle', 0.04);
  }

  private playExplosion(scale: number): void {
    this.playNoiseBurst(0.38 * scale, 280 - scale * 80, 0.05, 0.42 * scale);
    this.playToneSweep(90, 42, 0.36 * scale, 'sawtooth', 0.07 * scale);
  }

  private playImpact(): void {
    this.playNoiseBurst(0.08, 720, 0.01, 0.11);
    this.playToneSweep(220, 130, 0.12, 'triangle', 0.05);
  }

  private playLockAlert(): void {
    this.playToneSweep(1320, 1320, 0.08, 'square', 0.06);
    window.setTimeout(() => this.playToneSweep(980, 980, 0.08, 'square', 0.05), 120);
  }

  private playToneSweep(fromHz: number, toHz: number, duration: number, waveform: OscillatorType, level: number): void {
    if (!this.context || !this.fxBus) return;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = Math.max(fromHz, toHz) * 1.2;
    filter.Q.value = 1.4;
    osc.type = waveform;
    osc.frequency.setValueAtTime(fromHz, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, toHz), this.context.currentTime + duration);
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(level, this.context.currentTime + Math.min(0.02, duration * 0.4));
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.fxBus);
    osc.start();
    osc.stop(this.context.currentTime + duration + 0.02);
  }

  private playNoiseBurst(duration: number, frequency: number, attack: number, level: number): void {
    if (!this.context || !this.fxBus) return;
    const source = this.createNoiseSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    filter.type = 'bandpass';
    filter.frequency.value = frequency;
    filter.Q.value = 0.7;
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(level, this.context.currentTime + Math.max(0.005, attack));
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.fxBus);
    source.start();
    source.stop(this.context.currentTime + duration + 0.03);
  }
}
